import { BaseComponent } from "./base_component";
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data";

// Extend the NodeData type to include meta property
interface ExtendedNodeData extends NodeData {
  meta?: {
    isRunning?: boolean;
    [key: string]: any;
  };
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return typeof value === "object" && value !== null && typeof (value as PromiseLike<unknown>).then === "function"
}

function hasProcessMethod(value: unknown): value is { process: () => unknown } {
  return typeof value === "object" && value !== null && typeof (value as { process?: unknown }).process === "function"
}

/**
 * Adds a CSS class to highlight a node element
 */
function highlightNode(nodeId: number, addClass: boolean = true) {
  // Find the node element in the DOM
  const nodeElement = document.querySelector(`.node[data-id="${nodeId}"]`);
  if (nodeElement) {
    if (addClass) {
      nodeElement.classList.add('node-running');
    } else {
      nodeElement.classList.remove('node-running');
    }
  }
}

/**
 * Wraps components' worker methods with logging
 * This should only be called when debug mode is enabled
 * @param components Array of components to enhance with logging
 */
export function addLoggingToComponents<T extends BaseComponent[]>(components: T): T {
  components.forEach(component => {
    // Store reference to the original worker method
    const originalWorker = component.worker;
    
    // For all workers (both async and sync)
    component.worker = async function(
      node: NodeData, 
      inputs: WorkerInputs, 
      outputs: WorkerOutputs, 
      ...args: unknown[]
    ): Promise<void> {
      const startTime = new Date();
      const componentName = component.name;
      const nodeId = node.id;
      
      // Log the start of worker execution
      console.log(`Worker started for component: ${componentName}`, {
        nodeId,
        timestamp: startTime.toISOString()
      });
      
      // Set node running state
      const n = this.editor?.nodes.find(n => n.id === nodeId)
      if (!n) { return }
      n.meta.isRunning = true;
      n.update()
      
      try {
        // Always use await, even for sync functions (will just immediately resolve)
        await Promise.resolve(originalWorker.call(this, node, inputs, outputs, ...args));
        
        // If the node has "process" outputs that are promises, wait for those too
        const pendingPromises: Promise<any>[] = [];
        for (const key in outputs) {
          const output = outputs[key];
          if (isThenable(output)) {
            pendingPromises.push(Promise.resolve(output));
          } else if (hasProcessMethod(output)) {
            pendingPromises.push(Promise.resolve(output.process()));
          }
        }
        
        if (pendingPromises.length > 0) {
          await Promise.all(pendingPromises);
        }
        
        // Clear running state
        n.meta.isRunning = false;
        n.update()
        
        // Log after all promises are resolved
        console.log(`Worker fully completed for component: ${componentName}`, { 
          nodeId,
          executionTime: `${new Date().getTime() - startTime.getTime()}ms`,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        // Clear running state on error
        n.meta.isRunning = false;
        n.update()
        
        console.error(`Error in worker for component: ${componentName}`, {
          nodeId,
          error,
          executionTime: `${new Date().getTime() - startTime.getTime()}ms`,
          timestamp: new Date().toISOString()
        });
        throw error; // Re-throw to preserve error handling
      }
    } as typeof component.worker;
  });
  
  return components;
} 